/**
 * Export Trädgårdsmästare Invoice Admin Modal
 * Allows admin to select and export TG invoice data
 * Creates one worksheet per trädgårdsmästare
 */

import { useState, useMemo } from 'react'
import { X, Download, FileSpreadsheet, MapPin, Check, Loader2, Info, Leaf } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '../lib/supabase'
import { generateTGInvoiceAdminExcel, type TGInvoiceAdminExportAssignment, type ReportedArticleExport } from '../services/customerInvoiceExport'
import * as XLSX from 'xlsx'
import toast from 'react-hot-toast'

interface TGAssignment {
  id: string
  assignment_id: string
  assignment_number: number | null
  title: string
  start_date: string
  location: string | null
  tg_id: string
  tg_name: string
}

interface ExportTgInvoiceAdminModalProps {
  isOpen: boolean
  onClose: () => void
  tgAssignments: TGAssignment[]
  onExportComplete?: () => void
}

export function ExportTgInvoiceAdminModal({
  isOpen,
  onClose,
  tgAssignments,
  onExportComplete
}: ExportTgInvoiceAdminModalProps) {
  // Create unique key for each TG-assignment combination using | as separator (not in UUIDs)
  const getKey = (item: TGAssignment) => `${item.assignment_id}|${item.tg_id}`

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() =>
    new Set(tgAssignments.map(getKey))
  )
  const [isExporting, setIsExporting] = useState(false)

  // Group by TG
  const groupedByTG = useMemo(() => {
    const groups: Record<string, { tg_name: string; items: TGAssignment[] }> = {}
    for (const item of tgAssignments) {
      if (!groups[item.tg_id]) {
        groups[item.tg_id] = {
          tg_name: item.tg_name,
          items: []
        }
      }
      groups[item.tg_id].items.push(item)
    }
    // Sort groups by TG name
    return Object.entries(groups).sort(([, a], [, b]) =>
      a.tg_name.localeCompare(b.tg_name)
    )
  }, [tgAssignments])

  const toggleSelect = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedKeys(new Set(tgAssignments.map(getKey)))
  }

  const selectNone = () => {
    setSelectedKeys(new Set())
  }

  const handleExport = async () => {
    if (selectedKeys.size === 0) return

    setIsExporting(true)

    try {
      // Parse selected keys to get assignment_id and tg_id pairs
      // Using | as separator since UUIDs don't contain |
      const selectedPairs = Array.from(selectedKeys).map(key => {
        const [assignment_id, tg_id] = key.split('|')
        return { assignment_id, tg_id }
      })

      // Get unique TG IDs to fetch their price lists
      const tgIds = [...new Set(selectedPairs.map(p => p.tg_id))]

      // Fetch TG price lists
      const { data: tgData, error: tgError } = await supabase
        .from('subcontractors')
        .select('id, name, price_list_id')
        .in('id', tgIds)

      if (tgError) throw tgError

      // Build TG info map
      const tgInfoMap: Record<string, { name: string; price_list_id: string | null }> = {}
      for (const tg of tgData || []) {
        tgInfoMap[tg.id] = { name: tg.name, price_list_id: tg.price_list_id }
      }

      // Fetch all unique price list IDs
      const priceListIds = [...new Set(Object.values(tgInfoMap).map(t => t.price_list_id).filter(Boolean))] as string[]

      // Fetch prices from all relevant price lists
      const pricesByList: Record<string, Record<string, number>> = {}
      if (priceListIds.length > 0) {
        const { data: priceItems, error: priceError } = await supabase
          .from('price_list_articles')
          .select('price_list_id, article_id, price_excl_vat')
          .in('price_list_id', priceListIds)

        if (priceError) throw priceError

        for (const item of priceItems || []) {
          if (!pricesByList[item.price_list_id]) {
            pricesByList[item.price_list_id] = {}
          }
          pricesByList[item.price_list_id][item.article_id] = item.price_excl_vat
        }
      }

      // Fetch report data for selected TG-assignment pairs
      const assignmentIds = [...new Set(selectedPairs.map(p => p.assignment_id))]

      const { data: reportData, error: reportError } = await supabase
        .from('assignment_subcontractors')
        .select(`
          assignment_id,
          subcontractor_id,
          report_text,
          reported_articles,
          assignments (
            id,
            assignment_number,
            title,
            start_date,
            location
          )
        `)
        .in('assignment_id', assignmentIds)
        .in('subcontractor_id', tgIds)
        .eq('tg_status', 'godkand')

      if (reportError) throw reportError

      // Filter to only selected pairs (using | separator)
      const selectedKeySet = new Set(selectedKeys)
      const filteredReportData = (reportData || []).filter(row =>
        selectedKeySet.has(`${row.assignment_id}|${row.subcontractor_id}`)
      )

      // Build export data with tg_id for grouping
      const exportData: (TGInvoiceAdminExportAssignment & { tg_id: string })[] = filteredReportData.map(row => {
        const assignment = row.assignments as any
        const tgInfo = tgInfoMap[row.subcontractor_id]
        const priceListId = tgInfo?.price_list_id
        const priceMap = priceListId ? pricesByList[priceListId] || {} : {}

        const articles = (row.reported_articles as any[]) || []

        // Add prices from TG's price list to each article (preserve expense fields)
        const articlesWithPrices: ReportedArticleExport[] = articles.map(article => ({
          article_id: article.article_id,
          article_name: article.article_name,
          actual_quantity: article.actual_quantity,
          unit: article.unit || null,
          unit_price: priceMap[article.article_id] || null,
          notes: article.notes,
          // Utlägg-fält
          is_expense: article.is_expense || false,
          expense_amount_incl_vat: article.expense_amount_incl_vat || null,
          expense_amount_excl_vat: article.expense_amount_excl_vat || null,
          expense_vat_rate: article.expense_vat_rate ?? null
        }))

        return {
          id: assignment?.id,
          assignment_number: assignment?.assignment_number,
          title: assignment?.title || '',
          start_date: assignment?.start_date || '',
          location: typeof assignment?.location === 'string'
            ? assignment.location
            : (assignment?.location as any)?.address || null,
          tg_id: row.subcontractor_id,
          tg_name: tgInfo?.name || 'Okänd',
          report_text: row.report_text,
          reported_articles: articlesWithPrices
        }
      })

      // Sort by TG name, then by date
      exportData.sort((a, b) => {
        const tgCompare = a.tg_name.localeCompare(b.tg_name)
        if (tgCompare !== 0) return tgCompare
        return a.start_date.localeCompare(b.start_date)
      })

      // Generate and download Excel (with separate sheets per TG)
      const workbook = generateTGInvoiceAdminExcel(exportData)
      const dateStr = format(new Date(), 'yyyy-MM-dd')
      const filename = `tradgardsmastare-fakturering-${dateStr}.xlsx`
      XLSX.writeFile(workbook, filename)

      toast.success(`${selectedKeys.size} rapporter exporterade`)

      onExportComplete?.()
      onClose()
    } catch (err) {
      console.error('Export error:', err)
      toast.error('Kunde inte exportera data')
    } finally {
      setIsExporting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white dark:bg-nature-800 rounded-xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-nature-200 dark:border-nature-700">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Exportera trädgårdsmästarfakturering
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
          >
            <X className="h-5 w-5 text-nature-500" />
          </button>
        </div>

        {/* Info box */}
        <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Trädgårdsmästarfakturering (admin-vy)</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-300 text-xs">
                <li>Visar rapporter som godkänts</li>
                <li>Priser hämtas från trädgårdsmästarens prislista</li>
                <li>En flik per trädgårdsmästare i Excel-filen</li>
                <li>Markera som mottagen görs på uppdragssidan</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-amber-50 dark:bg-amber-900/20">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            <strong>OBS:</strong> Detta är ett automatiskt genererat underlag. Kontrollera att uppgifterna stämmer.
          </p>
        </div>

        {/* Selection controls */}
        <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900/50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-nature-600 dark:text-nature-400">
              {selectedKeys.size} av {tgAssignments.length} valda
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Välj alla
              </button>
              <button
                onClick={selectNone}
                className="px-2 py-1 text-xs rounded bg-nature-200 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-300 dark:hover:bg-nature-600 transition-colors"
              >
                Avmarkera alla
              </button>
            </div>
          </div>
        </div>

        {/* Assignment list grouped by TG */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {tgAssignments.length === 0 ? (
            <p className="text-center text-nature-500 dark:text-nature-400 py-8">
              Inga rapporter redo för export.
            </p>
          ) : (
            groupedByTG.map(([tgId, { tg_name, items }]) => (
              <div key={tgId}>
                {/* TG header */}
                <h3 className="text-sm font-medium text-nature-700 dark:text-nature-300 mb-2 flex items-center gap-1">
                  <Leaf className="h-4 w-4" />
                  {tg_name}
                </h3>

                {/* Assignments for this TG */}
                <div className="space-y-2">
                  {items.map(item => {
                    const key = getKey(item)
                    const isSelected = selectedKeys.has(key)
                    return (
                      <button
                        key={key}
                        onClick={() => toggleSelect(key)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'bg-green-600 border-green-600'
                              : 'border-nature-300 dark:border-nature-600'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>

                          {/* Assignment info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-nature-900 dark:text-nature-100 truncate">
                              {item.assignment_number && (
                                <span className="text-nature-500 dark:text-nature-400 mr-1">
                                  #{item.assignment_number}
                                </span>
                              )}
                              {item.title}
                            </h4>

                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-nature-500 dark:text-nature-400">
                              <span>{item.start_date}</span>
                              {item.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate max-w-[150px]">{item.location}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-nature-300 dark:border-nature-600 text-nature-700 dark:text-nature-300 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleExport}
            disabled={selectedKeys.size === 0 || isExporting}
            className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? 'Exporterar...' : `Exportera ${selectedKeys.size > 0 ? `(${selectedKeys.size})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
